// Visual type picker — replaces the bare <select> in the visual designer
// toolbar with a browsable modal: every catalog entry as a card with a
// representative icon and a one-line "what it is for" description, grouped by
// what the user is trying to show and filterable by a search box.
//
// The CATALOG stays the single source of truth (backend
// `DashboardVisuals.json` → `GET /api/designers/visuals-catalog`). This file
// only decorates it: icon + description + group, keyed by the canonical
// VisualName. An entry the backend adds later that isn't described here still
// shows up — under "More visuals" with a generic icon — so the picker can
// never hide a visual type.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  AlignEndHorizontal, CalendarDays, ChartArea, ChartBar, ChartColumn,
  ChartColumnStacked, ChartGantt, ChartLine, ChartNoAxesCombined, ChartPie,
  ChartScatter, ChevronDown, CircleDot, CircleDotDashed, CodeXml, CreditCard,
  FileCode2, FileText, Filter, Gauge, Grid2x2, Grid3x3, Layers, LayoutDashboard, ListTree,
  Map as MapIcon, Radar, Rows3, Rss, Search, Table, Waypoints, Check, X,
} from 'lucide-react'
import Modal from '@/components/common/Modal'
import { VISUAL_CATALOG, type VisualCatalogEntry } from './catalog'

/** Display groups, in the order they appear in the modal. */
const GROUP_ORDER = [
  'Tables & lists',
  'Comparison',
  'Trend',
  'Composition & flow',
  'Relationship & distribution',
  'KPI',
  'Location, time & hierarchy',
  'Custom output',
  'More visuals',
] as const
type GroupName = (typeof GROUP_ORDER)[number]

interface VisualTypeMeta {
  icon: LucideIcon
  description: string
  group: GroupName
}

const norm = (s: string | undefined) =>
  (s || '').toLowerCase().replace(/[\s_-]+/g, '')

/** Keyed by normalised VisualName (see `catalog.findCatalogEntry`). */
const META: Record<string, VisualTypeMeta> = {
  // ── Tables & lists ──
  tabular: {
    icon: Rows3, group: 'Tables & lists',
    description: 'A plain list of records, one row each.',
  },
  grid: {
    icon: Table, group: 'Tables & lists',
    description: 'Table with totals, grouping and multi-row select.',
  },
  pivot: {
    icon: Grid3x3, group: 'Tables & lists',
    description: 'Cross-tab of rows × columns × measures.',
  },
  feeds: {
    icon: Rss, group: 'Tables & lists',
    description: 'Activity or news style feed of records.',
  },

  // ── Comparison ──
  column: {
    icon: ChartColumn, group: 'Comparison',
    description: 'Compare a measure across categories, vertical bars.',
  },
  bar: {
    icon: ChartBar, group: 'Comparison',
    description: 'Same as Column, horizontal — good for long labels.',
  },
  stackedcolumn: {
    icon: ChartColumnStacked, group: 'Comparison',
    description: 'Totals split into a breakdown within each bar.',
  },
  columnline: {
    icon: ChartNoAxesCombined, group: 'Comparison',
    description: 'Columns plus a line, for two different measures.',
  },
  multistackline: {
    icon: Layers, group: 'Comparison',
    description: 'Multiple stacked series with overlay lines.',
  },

  // ── Trend ──
  line: {
    icon: ChartLine, group: 'Trend',
    description: 'How a measure moves over time.',
  },

  // ── Composition & flow ──
  pie: {
    icon: ChartPie, group: 'Composition & flow',
    description: 'Share of a whole across a few categories.',
  },
  doughnut: {
    icon: CircleDotDashed, group: 'Composition & flow',
    description: 'Share of a whole with a KPI in the centre.',
  },
  treemap: {
    icon: LayoutDashboard, group: 'Composition & flow',
    description: 'Nested rectangles sized by value, with drill levels.',
  },
  funnel: {
    icon: Filter, group: 'Composition & flow',
    description: 'Stage-by-stage conversion down a pipeline.',
  },
  waterfall: {
    icon: AlignEndHorizontal, group: 'Composition & flow',
    description: 'Signed +/− contributions bridging to a total.',
  },
  sankey: {
    icon: Waypoints, group: 'Composition & flow',
    description: 'Flow volume between two sets of entities.',
  },

  // ── Relationship & distribution ──
  scatter: {
    icon: ChartScatter, group: 'Relationship & distribution',
    description: 'Correlation between two measures, one dot per row.',
  },
  bubblechart: {
    icon: CircleDot, group: 'Relationship & distribution',
    description: 'Scatter with a third measure as the bubble size.',
  },
  heatmap: {
    icon: Grid2x2, group: 'Relationship & distribution',
    description: 'Matrix of cells coloured by density or value.',
  },
  radar: {
    icon: Radar, group: 'Relationship & distribution',
    description: 'Compare profiles across several axes at once.',
  },

  // ── KPI ──
  card: {
    icon: CreditCard, group: 'KPI',
    description: 'A single headline number, optionally with a sparkline.',
  },
  gauge: {
    icon: Gauge, group: 'KPI',
    description: 'Progress of a value toward its target.',
  },

  // ── Location, time & hierarchy ──
  map: {
    icon: MapIcon, group: 'Location, time & hierarchy',
    description: 'Points on a map, or regions shaded by a measure.',
  },
  calendar: {
    icon: CalendarDays, group: 'Location, time & hierarchy',
    description: 'Date-based events laid out on a calendar.',
  },
  taskscheduler: {
    icon: ChartGantt, group: 'Location, time & hierarchy',
    description: 'Tasks and jobs over time on a Gantt timeline.',
  },
  tree: {
    icon: ListTree, group: 'Location, time & hierarchy',
    description: 'Hierarchical data as an expandable tree.',
  },

  // ── Custom output ──
  html: {
    icon: CodeXml, group: 'Custom output',
    description: 'Your own HTML template rendered per row.',
  },
  xsl: {
    icon: FileCode2, group: 'Custom output',
    description: 'XML data transformed through an XSL stylesheet.',
  },
  document: {
    icon: FileText, group: 'Custom output',
    description: 'A laid-out document over these rows — designed in the '
      + 'document designer, one document per identity you name.',
  },
}

const FALLBACK_META: VisualTypeMeta = {
  icon: ChartArea,
  group: 'More visuals',
  description: 'Visual from the catalog.',
}

/** Icon + description + group for a catalog entry (never throws). */
export function visualTypeMeta(visualName: string | undefined): VisualTypeMeta {
  return META[norm(visualName)] || FALLBACK_META
}

/** Small reusable icon for a visual type — also usable outside this picker. */
export function VisualTypeIcon({
  visualName, size = 16,
}: { visualName: string | undefined; size?: number }) {
  const Icon = visualTypeMeta(visualName).icon
  return <Icon size={size} />
}

interface Props {
  value: string
  onChange: (visualName: string) => void
  disabled?: boolean
}

export default function VisualTypePicker({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  // The catalog is fetched on designer mount; read it through the live proxy.
  const entries: VisualCatalogEntry[] = useMemo(
    () => VISUAL_CATALOG.slice(),
    // Re-read whenever the modal opens so a late-arriving catalog is picked up.
    [open]
  )

  useEffect(() => {
    if (open) {
      setQuery('')
      // Autofocus after the modal paints.
      const t = window.setTimeout(() => searchRef.current?.focus(), 30)
      return () => window.clearTimeout(t)
    }
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries.filter((c) => {
      const meta = visualTypeMeta(c.VisualName)
      return (
        c.VisualName.toLowerCase().includes(q) ||
        meta.description.toLowerCase().includes(q) ||
        meta.group.toLowerCase().includes(q)
      )
    })
  }, [entries, query])

  const grouped = useMemo(() => {
    const buckets = new Map<GroupName, VisualCatalogEntry[]>()
    for (const c of filtered) {
      const g = visualTypeMeta(c.VisualName).group
      const list = buckets.get(g)
      if (list) list.push(c)
      else buckets.set(g, [c])
    }
    return GROUP_ORDER
      .filter((g) => (buckets.get(g)?.length || 0) > 0)
      .map((g) => ({ group: g, items: buckets.get(g) as VisualCatalogEntry[] }))
  }, [filtered])

  const select = (visualName: string) => {
    if (visualName !== value) onChange(visualName)
    setOpen(false)
  }

  const current = visualTypeMeta(value)
  const CurrentIcon = current.icon

  return (
    <>
      <style>{VTP_CSS}</style>
      <button
        type="button"
        className="vtp-trigger"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={`Visual type: ${value || 'none'} — click to change`}
      >
        <CurrentIcon size={15} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Choose…'}
        </span>
        <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Choose a visual type"
        size="full"
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{
            fontSize: 13.5, color: 'var(--color-text-faint)', marginBottom: 10,
          }}>
            Pick how this visual should present its data. Fields stay in any zone
            the new type also has; the rest move to the zone that suits them.
          </div>

          <div style={{ position: 'relative', marginBottom: 12 }}>
            <Search
              size={15}
              style={{
                position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)', color: 'var(--color-text-faint)',
                pointerEvents: 'none',
              }}
            />
            <input
              ref={searchRef}
              className="vtp-search"
              value={query}
              placeholder="Search visual types — trend, share, map, KPI…"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filtered.length > 0) {
                  e.preventDefault()
                  select(filtered[0].VisualName)
                }
              }}
            />
            {query && (
              <button
                type="button"
                className="vtp-clear"
                onClick={() => { setQuery(''); searchRef.current?.focus() }}
                title="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', maxHeight: '58vh', paddingRight: 4 }}>
            {grouped.length === 0 && (
              <div style={{
                padding: '28px 0', textAlign: 'center',
                fontSize: 14.5, color: 'var(--color-text-faint)',
              }}>
                No visual type matches “{query}”.
              </div>
            )}
            {grouped.map(({ group, items }) => (
              <div key={group} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, letterSpacing: '0.7px',
                  textTransform: 'uppercase', color: 'var(--color-text-faint)',
                  margin: '0 0 8px',
                }}>
                  {group}
                </div>
                <div style={{
                  display: 'grid', gap: 9,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))',
                }}>
                  {items.map((c) => {
                    const meta = visualTypeMeta(c.VisualName)
                    const Icon = meta.icon
                    const selected = c.VisualName === value
                    return (
                      <button
                        key={c.ID}
                        type="button"
                        className={`vtp-card${selected ? ' vtp-card-on' : ''}`}
                        onClick={() => select(c.VisualName)}
                        title={meta.description}
                      >
                        <span className="vtp-icon">
                          <Icon size={17} />
                        </span>
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span className="vtp-name">
                            {c.VisualName}
                            {selected && (
                              <Check size={13} style={{ color: 'var(--color-accent)' }} />
                            )}
                          </span>
                          <span className="vtp-desc">{meta.description}</span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  )
}

const VTP_CSS = `
.vtp-trigger {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 9px; max-width: 190px;
  font: inherit; font-size: 14px; font-weight: 600;
  color: var(--color-text); background: var(--color-input-bg);
  border: 1px solid var(--color-border); border-radius: 8px; cursor: pointer;
}
.vtp-trigger:hover:not(:disabled) { border-color: var(--color-accent); }
.vtp-trigger:disabled { opacity: 0.6; cursor: not-allowed; }
.vtp-search {
  width: 100%; padding: 9px 30px 9px 32px; font: inherit; font-size: 15px;
  color: var(--color-text); background: var(--color-input-bg);
  border: 1px solid var(--color-border); border-radius: 8px;
}
.vtp-search:focus { outline: none; border-color: var(--color-accent); }
.vtp-clear {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; padding: 0; cursor: pointer;
  color: var(--color-text-faint); background: transparent;
  border: none; border-radius: 5px;
}
.vtp-clear:hover { color: var(--color-text); background: var(--color-bg-secondary); }
.vtp-card {
  display: flex; align-items: flex-start; gap: 10px; text-align: left;
  padding: 10px 11px; font: inherit; cursor: pointer; min-width: 0;
  color: var(--color-text); background: var(--color-bg-secondary);
  border: 1px solid var(--color-border); border-radius: 10px;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.vtp-card:hover { border-color: var(--color-accent); background: var(--color-raised); }
.vtp-card-on { border-color: var(--color-accent); background: var(--color-accent-weak); }
.vtp-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; flex-shrink: 0; border-radius: 8px;
  color: var(--color-accent); background: var(--color-accent-weak);
}
.vtp-card-on .vtp-icon { color: #fff; background: var(--color-accent); }
.vtp-name {
  display: flex; align-items: center; gap: 5px;
  font-size: 14.5px; font-weight: 650; line-height: 1.25;
}
.vtp-desc {
  display: block; margin-top: 3px; font-size: 13px; font-weight: 400;
  line-height: 1.35; color: var(--color-text-faint);
}
`
